import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, cloudApi, supportApi } from '../services/api';
import { CLIENT_KEY_STORAGE, SCREENS } from '../lib/screenRegistry';
import {
  getMasterTestingKey,
  isMasterTestingKey,
  isMasterTestingSessionActive,
} from '../lib/masterTestingKey';
import { isMaintenancePreviewEmbed } from '../lib/previewEnvironment';
import {
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
  formatVoiceProfileLabel,
  resolveVoiceFromMatrix,
  previewVoiceSample as playVoicePreviewSample,
} from '../lib/voiceProfile';
import FileStoragePanel, { saveCloudStorageIntent } from '../components/FileStoragePanel';
import { DeleteAccountBlock, DELETE_ACCOUNT_SUCCESS_MESSAGE } from '../components/FileStorageModal';
import Tier2DocumentPicker from '../components/Tier2DocumentPicker';
import DesktopAppIconModal from '../components/DesktopAppIconModal';
import DesktopComputerIcon from '../components/DesktopComputerIcon';
import PhoneAccessQr from '../components/PhoneAccessQr';
import {
  DESKTOP_ICON_INSTALL_BUTTON,
  DESKTOP_ICON_MENU_LABEL,
  DESKTOP_ICON_MODAL_BODY,
} from '../lib/desktopAppIcon';
import { ORG_SHARED_FOLDER_PLACEHOLDER, buildCloudStorageMeta, googleDriveFolderUrl, isLocalIntentFileId } from '../lib/cloudStorage';
import { openMailDraftWindow, fillGmailDraft, describeGmailDraftResult } from '../lib/openMailDraft';
import './OrgAdmin.css';

const SIDEBAR_SECTIONS = ['Overview', 'Subscription', 'Enrollment', 'Org Shared Pack', 'Guardian Feed', 'Support'];
const ENROLLMENT_SUB_NAV = ['Mentor List', 'Archives'] as const;
type EnrollmentSubview = 'default' | 'mentor-list' | 'archives';

interface OrgSubscription {
  subscriptionState: string;
  renewStatus: string;
  renewDescription: string;
  cancelDescription: string;
  cancelStatus: string;
  isFieldTest: boolean;
  autoDeactivate: boolean;
}

async function copyOnboardingEmailPreview(
  preview: { recipientEmail?: string; subject: string; text: string },
) {
  const payload = [
    preview.recipientEmail ? `To: ${preview.recipientEmail}` : null,
    `Subject: ${preview.subject}`,
    '',
    preview.text,
  ].filter(Boolean).join('\n');
  await navigator.clipboard.writeText(payload);
}

interface OrgMentor {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  background_brief: string | null;
  activation_key: string;
  status: string;
  archived: number;
  created_at: string;
  menteeCount?: number;
  assignedMentees?: Array<{
    mentee_token: string;
    mentee_name?: string;
    mentee_email?: string;
  }>;
}

interface OrgMenteeKey {
  mentee_token: string;
  status: string;
  account_state: string;
  last_active: string;
  mentee_name?: string;
  mentee_email?: string;
  email?: string;
  mentor_name?: string;
  mentor_phone?: string;
  mentor_email?: string;
  mentor_id?: number | null;
}

interface GuardianAlert {
  id: number;
  mentee_token: string;
  crisis_type: string;
  mentor_email: string | null;
  mentor_notified: number;
  created_at: string;
  mentee_name: string | null;
  mentor_name: string | null;
}

type SharedPackFileCategory = 'General' | 'Emergency Protocol';

interface SharedPackFileEntry {
  id: string;
  filename: string;
  uploadedAt: string;
  category: SharedPackFileCategory;
}

export default function OrgAdmin() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window === 'undefined') return 'Overview';
    const fromQuery = new URLSearchParams(window.location.search).get('section');
    if (fromQuery && SIDEBAR_SECTIONS.includes(fromQuery)) return fromQuery;
    try {
      const saved = sessionStorage.getItem('mentology.orgAdmin.section');
      if (saved && SIDEBAR_SECTIONS.includes(saved)) return saved;
    } catch {
      /* ignore */
    }
    return 'Overview';
  });
  const [enrollmentSubview, setEnrollmentSubview] = useState<EnrollmentSubview>('default');
  const [dashboard, setDashboard] = useState<{
    org?: {
      id?: number;
      company_name: string;
      seat_count: number;
      admin_name: string;
      admin_email?: string;
      address?: string;
      phone?: string;
      contactTitle?: string;
      street?: string;
      state?: string;
      country?: string;
      postcode?: string;
      website?: string;
      emergencyContactName?: string;
      emergencyLeadEmail?: string;
      org_token?: string;
      product_code?: string;
      subscription?: OrgSubscription;
      cloud_storage?: {
        provider?: string;
        folder_label?: string;
        folder_id?: string | null;
        connected?: boolean;
        note?: string;
      };
      shared_pack_files?: Array<{
        drive_file_id?: string;
        id?: string;
        filename: string;
        selected_at?: string;
        category?: string;
      }>;
    };
    keys?: OrgMenteeKey[];
    mentors?: OrgMentor[];
    unassignedMentees?: OrgMenteeKey[];
    archivedMentors?: OrgMentor[];
    archivedMentees?: OrgMenteeKey[];
    guardianFeed?: GuardianAlert[];
    activeSeats?: number;
  } | null>(null);
  const [dashboardError, setDashboardError] = useState('');
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [guardianFeed, setGuardianFeed] = useState<GuardianAlert[]>([]);
  const [newKey, setNewKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrollError, setEnrollError] = useState('');
  const [enrollForm, setEnrollForm] = useState({
    menteeName: '',
    businessName: '',
    address: '',
    contactPhone: '',
    menteeEmail: '',
    businessDescription: '',
    dateEnrolled: '',
    mentorId: '',
    mentorName: '',
    mentorPhone: '',
    mentorEmail: '',
    languageTarget: '',
    aiMentorGender: '',
  });
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
  });
  const [supportFiles, setSupportFiles] = useState<File[]>([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState('');
  const [supportSuccess, setSupportSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [sharedPackFiles, setSharedPackFiles] = useState<SharedPackFileEntry[]>([]);
  // drag target removed — Drive picker handles selection when Google is connected
  const [redPhoneSaveStatus, setRedPhoneSaveStatus] = useState('');
  const [redPhoneContacts, setRedPhoneContacts] = useState({
    emergencyContactName: '',
    emergencyLeadEmail: '',
  });
  const [profileForm, setProfileForm] = useState({
    contactName: '',
    contactTitle: '',
    street: '',
    state: '',
    country: '',
    postcode: '',
    contactPhone: '',
    contactEmail: '',
    website: '',
  });
  const [profileSaveStatus, setProfileSaveStatus] = useState('');
  const profileDirtyRef = useRef(false);
  const profileFormRef = useRef(profileForm);
  profileFormRef.current = profileForm;
  const [clientKey, setClientKey] = useState('');
  const [mentorForm, setMentorForm] = useState({
    name: '',
    phone: '',
    email: '',
    backgroundBrief: '',
  });
  const [mentorLoading, setMentorLoading] = useState(false);
  const [mentorFormError, setMentorFormError] = useState('');
  const [mentorAssignError, setMentorAssignError] = useState('');
  const [copiedKey, setCopiedKey] = useState('');
  const [sendingEmailKey, setSendingEmailKey] = useState('');
  const [desktopIconOpen, setDesktopIconOpen] = useState(false);
  const [archiveActionLoading, setArchiveActionLoading] = useState<string | null>(null);
  const dashboardBootRef = useRef(false);

  const loadDashboard = useCallback(() => {
    setDashboardLoading(true);
    setDashboardError('');
    return authApi.orgDashboard()
      .then((data) => {
        setDashboard(data);
        if (data.guardianFeed) setGuardianFeed(data.guardianFeed);
        if (data.org?.cloud_storage) {
          saveCloudStorageIntent('org-admin', buildCloudStorageMeta(data.org.cloud_storage));
        }
        if (Array.isArray(data.org?.shared_pack_files)) {
          setSharedPackFiles(data.org.shared_pack_files.map((file: {
            drive_file_id?: string;
            id?: string;
            filename: string;
            selected_at?: string;
            category?: string;
          }) => ({
            id: String(file.drive_file_id || file.id || file.filename),
            filename: file.filename,
            uploadedAt: file.selected_at || '',
            category: file.category === 'Emergency Protocol' ? 'Emergency Protocol' : 'General',
          })));
        }
      })
      .catch((err: unknown) => {
        const payload = err && typeof err === 'object' ? (err as Record<string, unknown>) : {};
        const message =
          typeof payload.error === 'string'
            ? payload.error
            : typeof payload.message === 'string'
              ? payload.message
              : 'Organisation console failed to load.';
        const detail = typeof payload.detail === 'string' ? payload.detail : '';
        const inIframe = typeof window !== 'undefined' && window.self !== window.top;
        const hint = inIframe && /Organisation admin access required/i.test(message)
          ? ' Re-enter the Master Test Key at Portal Login after an app restart, then reopen Maintenance Preview.'
          : '';
        setDashboardError(detail ? `${message} (${detail})${hint}` : `${message}${hint}`);
        throw err;
      })
      .finally(() => {
        setDashboardLoading(false);
      });
  }, []);

  useEffect(() => {
    const storedKey = sessionStorage.getItem(CLIENT_KEY_STORAGE);
    if (storedKey) {
      setClientKey(storedKey);
    }
  }, []);

  useEffect(() => {
    if (dashboardBootRef.current) return;
    dashboardBootRef.current = true;

    const boot = async () => {
      const storedKey = (sessionStorage.getItem(CLIENT_KEY_STORAGE) || '').replace(/\s/g, '').toUpperCase();
      const isRealOrgKey = Boolean(storedKey)
        && storedKey !== 'ELT-DEV-9842'
        && !isMasterTestingKey(storedKey);

      // Only re-bind the Maintenance Preview sandbox when there is no real portal/client key.
      // A stored org token (ELT-MEN-6541, ELTDXXX, …) must win over a Master Admin cookie.
      if (!isRealOrgKey && isMaintenancePreviewEmbed()) {
        const masterKey = getMasterTestingKey();
        if (masterKey && isMasterTestingSessionActive()) {
          try {
            await authApi.login(masterKey);
          } catch {
            // Fall through to dashboard; error UI handles 403.
          }
        }
      }

      try {
        await loadDashboard();
      } catch (err: unknown) {
        const status =
          err && typeof err === 'object' && 'status' in err
            ? Number((err as { status: unknown }).status)
            : 0;
        const inIframe = typeof window !== 'undefined' && window.self !== window.top;
        if (!import.meta.env.DEV && !inIframe && (status === 401 || status === 403)) {
          navigate('/portal-login');
        }
      }
    };

    boot().catch(() => undefined);
  }, [loadDashboard, navigate]);

  useEffect(() => {
    if (!dashboard?.org) return;
    if (profileDirtyRef.current) return;
    const org = dashboard.org;
    setProfileForm({
      contactName: org.admin_name || '',
      contactTitle: org.contactTitle || '',
      street: org.street || org.address || '',
      state: org.state || '',
      country: org.country || '',
      postcode: org.postcode || '',
      contactPhone: org.phone || '',
      contactEmail: org.admin_email || '',
      website: org.website || '',
    });
    setRedPhoneContacts({
      emergencyContactName: org.emergencyContactName || org.admin_name || '',
      emergencyLeadEmail: org.emergencyLeadEmail || org.admin_email || '',
    });
  }, [dashboard]);

  useEffect(() => {
    if (activeSection !== 'Guardian Feed') return;
    const poll = () => {
      authApi.guardianFeed().then((data) => setGuardianFeed(data.guardianFeed || [])).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [activeSection]);

  useEffect(() => {
    try {
      sessionStorage.setItem('mentology.orgAdmin.section', activeSection);
    } catch {
      /* ignore */
    }
  }, [activeSection]);

  const updateProfile = (field: string, value: string) => {
    profileDirtyRef.current = true;
    setProfileForm((f) => ({ ...f, [field]: value }));
    setProfileSaveStatus('');
  };

  const persistProfile = useCallback(async (silent = false) => {
    if (!silent) setProfileSaveStatus('');
    try {
      const result = await authApi.saveOrgProfile(profileFormRef.current);
      profileDirtyRef.current = false;
      if (result?.org) {
        setDashboard((prev) => (prev ? { ...prev, org: { ...prev.org, ...result.org } } : prev));
      }
      setProfileSaveStatus('Contact details saved.');
      return true;
    } catch {
      setProfileSaveStatus('Unable to save contact details. Try again.');
      return false;
    }
  }, []);

  const saveOrgProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    await persistProfile(false);
  };

  useEffect(() => {
    if (!profileSaveStatus && !profileDirtyRef.current) return undefined;
    const timer = window.setTimeout(() => {
      if (profileDirtyRef.current) void persistProfile(true);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [profileForm, persistProfile, profileSaveStatus]);

  useEffect(() => {
    const flush = () => {
      if (profileDirtyRef.current) void persistProfile(true);
    };
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', flush);
    };
  }, [persistProfile]);

  const isEmergencyProtocolUploaded = sharedPackFiles.some(
    (file) => file.category === 'Emergency Protocol' && !isLocalIntentFileId(file.id),
  );

  const persistOrgCloudStorage = async (meta: {
    provider: string;
    folder_label: string;
    folder_id?: string | null;
    connected: boolean;
    note: string;
  }) => {
    await authApi.saveOrgCloudStorage(meta);
    try {
      const data = await authApi.orgDashboard();
      setDashboard(data);
    } catch {
      // Folder save already succeeded; picker will refresh its own cloud status.
    }
  };

  const updateRedPhoneContact = (field: string, value: string) => {
    setRedPhoneContacts((f) => ({ ...f, [field]: value }));
  };

  const resetRedPhoneContacts = () => {
    setRedPhoneContacts({
      emergencyContactName: '',
      emergencyLeadEmail: '',
    });
  };

  const updateRedPhoneContacts = async (e: React.FormEvent) => {
    e.preventDefault();
    setRedPhoneSaveStatus('');
    try {
      const result = await authApi.saveOrgProfile({
        emergencyContactName: redPhoneContacts.emergencyContactName,
        emergencyLeadEmail: redPhoneContacts.emergencyLeadEmail,
      });
      if (result?.org) {
        setDashboard((prev) => (prev ? { ...prev, org: { ...prev.org, ...result.org } } : prev));
      }
      setRedPhoneSaveStatus('RED Email contacts saved. Built-in protocols will use this name and email.');
    } catch {
      setRedPhoneSaveStatus('Unable to save RED Email contacts.');
    }
  };

  const removeSharedPackFile = async (fileId: string) => {
    const remaining = sharedPackFiles.filter((file) => file.id !== fileId);
    try {
      const saved = await cloudApi.saveSelectedFiles({
        scope: 'org',
        files: remaining.map((file) => ({
          drive_file_id: file.id,
          filename: file.filename,
          category: file.category,
          selected_at: file.uploadedAt,
        })),
      });
      const files = (saved.files || remaining) as Array<{
        drive_file_id?: string;
        id?: string;
        filename: string;
        selected_at?: string;
        category?: string;
      }>;
      setSharedPackFiles(files.map((file) => ({
        id: String(file.drive_file_id || file.id || file.filename),
        filename: file.filename,
        uploadedAt: file.selected_at || '',
        category: (file.category === 'Emergency Protocol' ? 'Emergency Protocol' : 'General') as SharedPackFileCategory,
      })));
    } catch {
      window.alert('Unable to remove that file from Mentology.');
    }
  };

  const updateEnroll = (field: string, value: string) => {
    setEnrollForm((f) => ({ ...f, [field]: value }));
  };

  const selectAssignedMentor = (mentorIdValue: string) => {
    const mentors = dashboard?.mentors ?? [];
    const mentor = mentors.find((row) => String(row.id) === mentorIdValue);
    setEnrollForm((f) => ({
      ...f,
      mentorId: mentorIdValue,
      mentorName: mentor?.name || '',
      mentorPhone: mentor?.phone || '',
      mentorEmail: mentor?.email || '',
    }));
  };

  const resetEnrollForm = () => {
    setEnrollForm({
      menteeName: '',
      businessName: '',
      address: '',
      contactPhone: '',
      menteeEmail: '',
      businessDescription: '',
      dateEnrolled: '',
      mentorId: '',
      mentorName: '',
      mentorPhone: '',
      mentorEmail: '',
      languageTarget: '',
      aiMentorGender: '',
    });
    setNewKey('');
    setEnrollError('');
  };

  const simulateCrmDataPush = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEnrollForm((f) => ({
      ...f,
      menteeName: 'John Citizen',
      businessName: 'Citizen Consulting Pty Ltd',
      address: '123 Ocean Drive, Noosa Heads QLD 4567',
      contactPhone: '+61 412 345 678',
      menteeEmail: 'john@citizenconsulting.com.au',
      businessDescription: 'B2B Professional Services and Scale-up Advisory Operations',
      dateEnrolled: today,
      languageTarget: '',
      aiMentorGender: '',
      mentorId: '',
      mentorName: '',
      mentorPhone: '',
      mentorEmail: '',
    }));
    setEnrollError('');
  };

  const handleEnrollmentVoicePreview = async () => {
    const entry = resolveVoiceFromMatrix(enrollForm.languageTarget, enrollForm.aiMentorGender);
    if (!entry) return;

    try {
      await playVoicePreviewSample(entry.voiceId, entry.name, {
        language: entry.language,
        gender: entry.gender,
        modelId: entry.modelId,
      });
    } catch (error) {
      console.error('[Voice preview]', error);
    }
  };

  const updateSupport = (field: string, value: string) => {
    setSupportForm((f) => ({ ...f, [field]: value }));
    setSupportSuccess('');
  };

  const addSupportFiles = (files: FileList | null) => {
    if (!files) return;
    setSupportFiles((prev) => [...prev, ...Array.from(files)]);
    setSupportSuccess('');
  };

  const readFileAsBase64 = (file: File) =>
    new Promise<{ filename: string; mimeType: string; size: number; data: string }>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
          data: result.split(',')[1] || '',
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const submitSupportTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setSupportLoading(true);
    setSupportError('');
    setSupportSuccess('');
    try {
      const attachments = await Promise.all(supportFiles.map(readFileAsBase64));
      const result = await supportApi.submitTicket({
        ...supportForm,
        attachments,
      });
      const routedTo = result.routedTo || 'support@mentology.com.au';
      if (result.mailed) {
        setSupportSuccess(result.message || `Support ticket emailed to ${routedTo}.`);
      } else {
        setSupportSuccess('');
        setSupportError(
          `${result.message || 'Ticket saved.'} SMTP did not deliver to ${routedTo}. Open Master Admin → System Data to read the ticket, or send the same details from your mailbox to ${routedTo}.`,
        );
      }
      setSupportForm({ name: '', email: '', phone: '', description: '' });
      setSupportFiles([]);
    } catch (err: unknown) {
      setSupportError((err as { error?: string }).error || 'Support ticket submission failed.');
    } finally {
      setSupportLoading(false);
    }
  };

  const updateMentorForm = (field: string, value: string) => {
    setMentorForm((f) => ({ ...f, [field]: value }));
    setMentorFormError('');
  };

  const resetMentorForm = () => {
    setMentorForm({ name: '', phone: '', email: '', backgroundBrief: '' });
    setMentorFormError('');
  };

  const createMentor = async (e: React.FormEvent) => {
    e.preventDefault();
    setMentorLoading(true);
    setMentorFormError('');
    try {
      await authApi.createMentor(mentorForm);
      resetMentorForm();
      await loadDashboard();
    } catch (err: unknown) {
      setMentorFormError((err as { error?: string }).error || 'Mentor creation failed.');
    } finally {
      setMentorLoading(false);
    }
  };

  const copyMentorKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 2000);
    } catch {
      setCopiedKey('');
    }
  };

  const copyMenteeEmail = async (menteeToken: string) => {
    try {
      const preview = await authApi.getMenteeEmailPreview(menteeToken);
      await copyOnboardingEmailPreview(preview);
      window.alert('Onboarding email copied. Paste into your mail client if you prefer not to use SEND.');
    } catch {
      window.alert('Unable to copy onboarding email for this mentee.');
    }
  };

  const sendMenteeEmail = async (menteeToken: string, menteeEmail?: string) => {
    if (!menteeEmail) {
      window.alert('No mentee email on file.');
      return;
    }
    const draftWin = openMailDraftWindow();
    if (!draftWin) {
      window.alert('Allow pop-ups for this site so Gmail can open a draft. Then click SEND again.');
      return;
    }
    setSendingEmailKey(`mentee-${menteeToken}`);
    try {
      const preview = await authApi.getMenteeEmailPreview(menteeToken);
      const opened = await fillGmailDraft(draftWin, {
        to: String(preview.recipientEmail || menteeEmail || '').trim(),
        subject: String(preview.subject || 'Your Mentology Mentee Access Key').trim(),
        body: preview.text,
      });
      window.alert(describeGmailDraftResult(opened));
    } catch {
      try { draftWin.close(); } catch { /* ignore */ }
      window.alert('Unable to open the onboarding email in Gmail Drafts.');
    } finally {
      setSendingEmailKey('');
    }
  };

  const copyMentorEmail = async (mentorId: number) => {
    try {
      const preview = await authApi.getMentorEmailPreview(mentorId);
      await copyOnboardingEmailPreview(preview);
      window.alert('Onboarding email copied. Paste into your mail client if you prefer not to use SEND.');
    } catch {
      window.alert('Unable to copy onboarding email for this mentor.');
    }
  };

  const sendMentorEmail = async (mentorId: number, mentorEmail?: string | null) => {
    if (!mentorEmail) {
      window.alert('No mentor email on file.');
      return;
    }
    const draftWin = openMailDraftWindow();
    if (!draftWin) {
      window.alert('Allow pop-ups for this site so Gmail can open a draft. Then click SEND again.');
      return;
    }
    setSendingEmailKey(`mentor-${mentorId}`);
    try {
      const preview = await authApi.getMentorEmailPreview(mentorId);
      const opened = await fillGmailDraft(draftWin, {
        to: String(preview.recipientEmail || mentorEmail || '').trim(),
        subject: String(preview.subject || 'Your Mentology Mentor Activation Key').trim(),
        body: preview.text,
      });
      window.alert(describeGmailDraftResult(opened));
    } catch {
      try { draftWin.close(); } catch { /* ignore */ }
      window.alert('Unable to open the onboarding email in Gmail Drafts.');
    } finally {
      setSendingEmailKey('');
    }
  };

  const handleAssignMentee = async (mentorId: number, menteeToken: string) => {
    if (!menteeToken) return;
    setMentorAssignError('');
    try {
      await authApi.assignMenteeToMentor(mentorId, menteeToken);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorAssignError((err as { error?: string }).error || 'Mentee assignment failed.');
    }
  };

  const handleArchiveMentor = async (mentorId: number) => {
    setArchiveActionLoading(`mentor-${mentorId}`);
    try {
      await authApi.archiveMentor(mentorId);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorAssignError((err as { error?: string }).error || 'Mentor archive failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const handleRestoreMentor = async (mentorId: number) => {
    setArchiveActionLoading(`restore-mentor-${mentorId}`);
    try {
      await authApi.restoreMentor(mentorId);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorAssignError((err as { error?: string }).error || 'Mentor restore failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const handleRestoreMentee = async (menteeToken: string) => {
    setArchiveActionLoading(`restore-mentee-${menteeToken}`);
    try {
      await authApi.restoreMentee(menteeToken);
      await loadDashboard();
    } catch (err: unknown) {
      setMentorAssignError((err as { error?: string }).error || 'Mentee restore failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const handleArchiveMentee = async (menteeToken: string) => {
    setArchiveActionLoading(`archive-mentee-${menteeToken}`);
    try {
      await authApi.archiveMentee(menteeToken);
      await loadDashboard();
    } catch (err: unknown) {
      setEnrollError((err as { error?: string }).error || 'Mentee archive failed.');
    } finally {
      setArchiveActionLoading(null);
    }
  };

  const enrollMentee = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setEnrollError('');
    setNewKey('');
    try {
      const result = await authApi.enrollMentee({
        menteeName: enrollForm.menteeName,
        menteeEmail: enrollForm.menteeEmail,
        mentorId: enrollForm.mentorId ? Number(enrollForm.mentorId) : undefined,
        mentorName: enrollForm.mentorName,
        mentorPhone: enrollForm.mentorPhone,
        mentorEmail: enrollForm.mentorEmail,
      });
      setNewKey(result.menteeToken);
      await loadDashboard();
      window.alert(
        `Mentee enrolled as ${result.menteeToken}.\n\nNo email was sent.\n\nClick SEND to open the message in your mailbox, edit it, then send from there.`,
      );
    } catch (err: unknown) {
      setEnrollError((err as { error?: string }).error || 'Mentee enrollment failed.');
    } finally {
      setLoading(false);
    }
  };

  const renderPanel = () => {
    switch (activeSection) {
      case 'Overview': {
        const seatLimit = dashboard?.org?.seat_count ?? 10;
        const activeSeats = dashboard?.activeSeats ?? 0;
        const orgName = dashboard?.org?.company_name || '—';
        const orgId = dashboard?.org?.org_token || clientKey || '—';
        const productCode = dashboard?.org?.product_code || 'prod_UExfDU5FMDJ9vK';
        const subscription = dashboard?.org?.subscription;
        const subscriptionLabel = subscription?.subscriptionState || 'Active';

        return (
          <div className="overview-layout">
            <div className="org-access-strip">
              <div className="org-access-desktop">
                <DesktopComputerIcon className="org-access-desktop-icon" />
                <div className="org-access-desktop-copy">
                  <h3 className="org-access-title">{DESKTOP_ICON_MENU_LABEL}</h3>
                  <p className="scannable-bullet-text">{DESKTOP_ICON_MODAL_BODY}</p>
                  <button
                    type="button"
                    className="btn-primary org-access-desktop-btn"
                    onClick={() => setDesktopIconOpen(true)}
                  >
                    {DESKTOP_ICON_INSTALL_BUTTON}
                  </button>
                </div>
              </div>
              <PhoneAccessQr titleId="org-admin-phone-qr-title" compact />
            </div>

            <div className="org-panel-card overview-metrics-card">
              <h2 className="section-header overview-section-heading">Mentoring Control</h2>
              <div className="overview-metrics-stack">
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Organisation:</span> {orgName}
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Org-ID:</span>{' '}
                  <span className="overview-id-box">{orgId}</span>
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Product:</span> Mentology Mentoring App
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Seats:</span> {seatLimit} ({activeSeats} used)
                </p>
                <p className="overview-metric-line">
                  <span className="overview-metric-label">Subscription Status:</span> {subscriptionLabel}
                </p>
              </div>
            </div>

            <div className="org-panel-card overview-ai-card">
              <h3 className="section-header overview-ai-heading">AI Alignment</h3>
              <p className="overview-ai-copy">
                The AI-Mentor already works through the Mentology framework. There is nothing to
                select on this screen. Organisation policies and learning files are chosen in
                Org Shared Pack.
              </p>
            </div>

            <div className="org-panel-card overview-profile-card">
              <form className="overview-profile-form" onSubmit={saveOrgProfile}>
                <div className="overview-profile-actions overview-profile-actions-top">
                  <button type="submit" className="btn-primary overview-save-profile-btn">
                    SAVE CONTACT DETAILS
                  </button>
                  {profileSaveStatus && (
                    <p className="overview-profile-save-status" role="status">{profileSaveStatus}</p>
                  )}
                </div>
                <p className="overview-profile-save-hint">
                  Click Save (or wait a moment — details auto-save). Connecting Google Drive will not erase saved contact details.
                </p>
                <div className="overview-form-row overview-form-row-2">
                  <label className="overview-field-label">
                    Contact Name
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.contactName}
                      onChange={(e) => updateProfile('contactName', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Contact Title
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.contactTitle}
                      onChange={(e) => updateProfile('contactTitle', e.target.value)}
                    />
                  </label>
                </div>

                <p className="overview-section-label">Business Address</p>

                <label className="overview-field-label">
                  Street
                  <input
                    className="input-field overview-input"
                    type="text"
                    value={profileForm.street}
                    onChange={(e) => updateProfile('street', e.target.value)}
                  />
                </label>

                <div className="overview-form-row overview-form-row-3">
                  <label className="overview-field-label">
                    State
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.state}
                      onChange={(e) => updateProfile('state', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Country
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.country}
                      onChange={(e) => updateProfile('country', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Postcode
                    <input
                      className="input-field overview-input"
                      type="text"
                      value={profileForm.postcode}
                      onChange={(e) => updateProfile('postcode', e.target.value)}
                    />
                  </label>
                </div>

                <div className="overview-form-row overview-form-row-3">
                  <label className="overview-field-label">
                    Contact Phone
                    <input
                      className="input-field overview-input"
                      type="tel"
                      value={profileForm.contactPhone}
                      onChange={(e) => updateProfile('contactPhone', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Contact Email
                    <input
                      className="input-field overview-input"
                      type="email"
                      value={profileForm.contactEmail}
                      onChange={(e) => updateProfile('contactEmail', e.target.value)}
                    />
                  </label>
                  <label className="overview-field-label">
                    Organisation Website
                    <input
                      className="input-field overview-input"
                      type="url"
                      value={profileForm.website}
                      onChange={(e) => updateProfile('website', e.target.value)}
                    />
                  </label>
                </div>

                <div className="overview-profile-actions">
                  <button type="submit" className="btn-primary overview-save-profile-btn">
                    SAVE CONTACT DETAILS
                  </button>
                  {profileSaveStatus && (
                    <p className="overview-profile-save-status" role="status">{profileSaveStatus}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        );
      }
      case 'Subscription': {
        const seatLimit = dashboard?.org?.seat_count ?? 10;
        const subscription = dashboard?.org?.subscription;
        const isFieldTest = subscription?.isFieldTest ?? false;
        const renewStatus = subscription?.renewStatus ?? '—';
        const renewDescription = subscription?.renewDescription ?? 'Convert pilot to full subscription';
        const cancelDescription = subscription?.cancelDescription ?? 'Deactivate at end of billing period';
        const cancelStatus = subscription?.cancelStatus ?? 'Active';

        return (
          <div className="org-panel-card subscription-governance-card">
            <h2 className="section-header subscription-governance-heading">Subscription Governance</h2>
            {isFieldTest && (
              <p className="subscription-field-test-banner" role="status">
                Field test organisation — access continues until Master Admin ends it. No automatic countdown or deactivation.
              </p>
            )}
            <table className="subscription-governance-table">
              <tbody>
                <tr>
                  <td className="subscription-col-action">Renew / Lock-In</td>
                  <td className="subscription-col-description">{renewDescription}</td>
                  <td className="subscription-col-status">{renewStatus}</td>
                  <td className="subscription-col-button">
                    <button
                      type="button"
                      className="subscription-action-btn subscription-action-btn-primary"
                      disabled={isFieldTest}
                    >
                      Renew
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className="subscription-col-action">Upgrade Plan</td>
                  <td className="subscription-col-description">Increase seat limit</td>
                  <td className="subscription-col-status">Current: {seatLimit} seats</td>
                  <td className="subscription-col-button">
                    <button type="button" className="subscription-action-btn subscription-action-btn-primary">
                      Upgrade
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className="subscription-col-action">Cancel</td>
                  <td className="subscription-col-description">{cancelDescription}</td>
                  <td className="subscription-col-status">{cancelStatus}</td>
                  <td className="subscription-col-button">
                    <button
                      type="button"
                      className="subscription-action-btn subscription-action-btn-warning"
                      disabled={isFieldTest}
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
                <tr>
                  <td className="subscription-col-action">Purge Workspace</td>
                  <td className="subscription-col-description">Delete all files</td>
                  <td className="subscription-col-status">Irreversible</td>
                  <td className="subscription-col-button">
                    <button type="button" className="subscription-action-btn subscription-action-btn-danger">
                      Delete All Files
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
      case 'Enrollment': {
        if (enrollmentSubview === 'archives') {
          const archivedMentors = dashboard?.archivedMentors ?? [];
          const archivedMentees = dashboard?.archivedMentees ?? [];

          return (
            <div className="org-panel-card archives-workspace admin-canvas-swap">
              <h2 className="section-header archives-heading">Historical Archives</h2>
              <p className="archives-subtext">Restore archived mentors and mentees to reactivate their live directory slots.</p>
              {mentorAssignError && <p className="error-text archives-form-error">{mentorAssignError}</p>}

              <div className="archives-dual-pane">
                <div className="archives-pane">
                  <h3 className="archives-pane-title">Archived Mentors Directory</h3>
                  <table className="admin-table archives-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Key</th>
                        <th>Email</th>
                        <th>Brief</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedMentors.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="archives-empty-row">No archived mentors.</td>
                        </tr>
                      ) : (
                        archivedMentors.map((mentor) => (
                          <tr key={mentor.id}>
                            <td>{mentor.name}</td>
                            <td className="mentor-key-cell">{mentor.activation_key}</td>
                            <td>{mentor.email || '—'}</td>
                            <td className="mentor-brief-cell">{mentor.background_brief || '—'}</td>
                            <td>
                              <button
                                type="button"
                                className="mentor-restore-btn"
                                disabled={archiveActionLoading === `restore-mentor-${mentor.id}`}
                                onClick={() => handleRestoreMentor(mentor.id)}
                              >
                                {archiveActionLoading === `restore-mentor-${mentor.id}` ? 'Restoring...' : 'RESTORE'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="archives-pane">
                  <h3 className="archives-pane-title">Archived Mentees Directory</h3>
                  <table className="admin-table archives-table">
                    <thead>
                      <tr>
                        <th>Mentee</th>
                        <th>ELT Key</th>
                        <th>Mentor</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedMentees.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="archives-empty-row">No archived mentees.</td>
                        </tr>
                      ) : (
                        archivedMentees.map((mentee) => (
                          <tr key={mentee.mentee_token}>
                            <td>{mentee.mentee_name || mentee.mentee_email || '—'}</td>
                            <td>{mentee.mentee_token}</td>
                            <td>{mentee.mentor_name || '—'}</td>
                            <td>{mentee.status}</td>
                            <td>
                              <button
                                type="button"
                                className="mentor-restore-btn"
                                disabled={archiveActionLoading === `restore-mentee-${mentee.mentee_token}`}
                                onClick={() => handleRestoreMentee(mentee.mentee_token)}
                              >
                                {archiveActionLoading === `restore-mentee-${mentee.mentee_token}` ? 'Restoring...' : 'RESTORE'}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        }

        if (enrollmentSubview === 'mentor-list') {
          const mentors = dashboard?.mentors ?? [];
          const unassignedMentees = dashboard?.unassignedMentees ?? [];

          return (
            <div className="org-panel-card mentor-list-workspace admin-canvas-swap">
              <h2 className="section-header mentor-list-heading">Mentor Administration</h2>
              <p className="mentor-list-subtext">Create mentor activation keys and manage live mentor assignments.</p>

              <form onSubmit={createMentor} className="mentor-create-card">
                <p className="mentor-create-title">Add New Mentor</p>
                <div className="mentor-create-grid">
                  <label className="enrollment-field-label">
                    Name
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={mentorForm.name}
                      onChange={(e) => updateMentorForm('name', e.target.value)}
                      required
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Phone
                    <input
                      className="input-field enrollment-field-input"
                      type="tel"
                      value={mentorForm.phone}
                      onChange={(e) => updateMentorForm('phone', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Email
                    <input
                      className="input-field enrollment-field-input"
                      type="email"
                      value={mentorForm.email}
                      onChange={(e) => updateMentorForm('email', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label mentor-brief-field">
                    Background Brief
                    <textarea
                      className="input-field enrollment-field-input enrollment-textarea"
                      value={mentorForm.backgroundBrief}
                      onChange={(e) => updateMentorForm('backgroundBrief', e.target.value)}
                      rows={2}
                    />
                  </label>
                </div>
                {mentorFormError && <p className="error-text mentor-form-error">{mentorFormError}</p>}
                <button type="submit" className="mentor-create-key-btn" disabled={mentorLoading}>
                  {mentorLoading ? 'Creating...' : 'CREATE MENTOR KEY'}
                </button>
              </form>

              <h3 className="mentor-directory-title">Active Mentor Directory</h3>
              <div className="mentor-directory-scroll">
                <table className="admin-table mentor-directory-table">
                  <thead>
                    <tr>
                      <th>Mentor Name</th>
                      <th>Generated Key</th>
                      <th>Status</th>
                      <th>Mentees</th>
                      <th>Assign New Mentee</th>
                      <th>Email</th>
                      <th>Archive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mentors.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="mentor-directory-empty">No active mentors yet.</td>
                      </tr>
                    ) : (
                      mentors.map((mentor) => (
                        <tr key={mentor.id}>
                          <td>{mentor.name}</td>
                          <td>
                            <button
                              type="button"
                              className="mentor-key-copy-btn"
                              title="Copy activation key"
                              onClick={() => copyMentorKey(mentor.activation_key)}
                            >
                              {mentor.activation_key}
                              {copiedKey === mentor.activation_key ? ' ✓' : ''}
                            </button>
                          </td>
                          <td>
                            <span className={`mentor-status-badge mentor-status-${mentor.status.toLowerCase()}`}>
                              {mentor.status}
                            </span>
                          </td>
                          <td>
                            <div className="mentor-assigned-cell">
                              <span>{mentor.menteeCount ?? 0}</span>
                              {(mentor.assignedMentees || []).length > 0 && (
                                <ul className="mentor-assigned-names">
                                  {(mentor.assignedMentees || []).map((mentee) => (
                                    <li key={mentee.mentee_token}>
                                      {mentee.mentee_name || mentee.mentee_email || mentee.mentee_token}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </td>
                          <td>
                            <select
                              className="input-field mentor-assign-select"
                              defaultValue=""
                              onChange={(e) => {
                                const token = e.target.value;
                                if (token) {
                                  void handleAssignMentee(mentor.id, token);
                                  e.target.value = '';
                                }
                              }}
                            >
                              <option value="">Select unassigned mentee</option>
                              {unassignedMentees.map((mentee) => (
                                <option key={mentee.mentee_token} value={mentee.mentee_token}>
                                  {mentee.mentee_name || mentee.mentee_email || mentee.mentee_token}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <div className="org-email-action-stack">
                              {mentor.email ? (
                                <>
                                  <span className="org-email-address">{mentor.email}</span>
                                  <button
                                    type="button"
                                    className="org-email-action-btn"
                                    onClick={() => void copyMentorEmail(mentor.id)}
                                  >
                                    COPY EMAIL
                                  </button>
                                  <button
                                    type="button"
                                    className="org-email-action-btn org-email-action-btn-send"
                                    disabled={sendingEmailKey === `mentor-${mentor.id}`}
                                    onClick={() => void sendMentorEmail(mentor.id, mentor.email)}
                                  >
                                    {sendingEmailKey === `mentor-${mentor.id}` ? 'Opening…' : 'SEND TO MENTOR'}
                                  </button>
                                </>
                              ) : (
                                '—'
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="mentor-archive-btn"
                              disabled={archiveActionLoading === `mentor-${mentor.id}`}
                              onClick={() => handleArchiveMentor(mentor.id)}
                            >
                              {archiveActionLoading === `mentor-${mentor.id}` ? '...' : 'ARCHIVE'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {mentorAssignError && <p className="error-text mentor-assign-error">{mentorAssignError}</p>}
            </div>
          );
        }

        const activeSeats = dashboard?.activeSeats ?? 0;
        const seatLimit = dashboard?.org?.seat_count ?? 10;
        const enrollmentMentors = dashboard?.mentors ?? [];
        const selectedMentorLocked = Boolean(enrollForm.mentorId);
        const voiceEntry = resolveVoiceFromMatrix(enrollForm.languageTarget, enrollForm.aiMentorGender);
        const voiceProfile = voiceEntry ? formatVoiceProfileLabel(voiceEntry) : null;

        return (
          <div className="org-panel-card enrollment-workspace admin-canvas-swap">
            <div className="enrollment-card-header">
              <div className="enrollment-card-header-text">
                <h2 className="section-header enrollment-heading">Mentee Enrolment</h2>
                <p className="enrollment-subtext">Manage the individual Mentees.</p>
              </div>
              <button
                type="button"
                className="enrollment-crm-sim-btn"
                onClick={simulateCrmDataPush}
              >
                [ 🔌 Simulate CRM Data Push ]
              </button>
            </div>

            <form onSubmit={enrollMentee} className="enrollment-form-stack">
              <div className="enrollment-setup-grid">
                <div className="enrollment-setup-col enrollment-setup-col-left">
                  <label className="enrollment-field-label">
                    Mentee Name
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.menteeName}
                      onChange={(e) => updateEnroll('menteeName', e.target.value)}
                      required
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Business Name
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.businessName}
                      onChange={(e) => updateEnroll('businessName', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Address
                    <input
                      className="input-field enrollment-field-input"
                      type="text"
                      value={enrollForm.address}
                      onChange={(e) => updateEnroll('address', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Contact Phone
                    <input
                      className="input-field enrollment-field-input"
                      type="tel"
                      value={enrollForm.contactPhone}
                      onChange={(e) => updateEnroll('contactPhone', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Mentee Email
                    <input
                      className="input-field enrollment-field-input"
                      type="email"
                      value={enrollForm.menteeEmail}
                      onChange={(e) => updateEnroll('menteeEmail', e.target.value)}
                      required
                    />
                  </label>
                </div>

                <div className="enrollment-setup-col enrollment-setup-col-right">
                  <label className="enrollment-field-label">
                    Description of Business
                    <textarea
                      className="input-field enrollment-field-input enrollment-textarea"
                      value={enrollForm.businessDescription}
                      onChange={(e) => updateEnroll('businessDescription', e.target.value)}
                      rows={2}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Date Enrolled
                    <input
                      className="input-field enrollment-field-input"
                      type="date"
                      value={enrollForm.dateEnrolled}
                      onChange={(e) => updateEnroll('dateEnrolled', e.target.value)}
                    />
                  </label>
                  <label className="enrollment-field-label">
                    Language Target
                    <select
                      className="input-field enrollment-field-input enrollment-field-select"
                      value={enrollForm.languageTarget}
                      onChange={(e) => updateEnroll('languageTarget', e.target.value)}
                    >
                      <option value="">Select language</option>
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="enrollment-field-label">
                    AI-Mentor Gender
                    <select
                      className="input-field enrollment-field-input enrollment-field-select"
                      value={enrollForm.aiMentorGender}
                      onChange={(e) => updateEnroll('aiMentorGender', e.target.value)}
                    >
                      <option value="">Select gender</option>
                      {GENDER_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  {voiceProfile && (
                    <div className="enrollment-voice-preview-bar">
                      <span className="enrollment-voice-profile-label">
                        Voice Profile: {voiceProfile}
                      </span>
                      <button
                        type="button"
                        className="enrollment-voice-preview-btn"
                        onClick={handleEnrollmentVoicePreview}
                      >
                        [ ▶ Preview Voice Sample ]
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="enrollment-section-break" aria-hidden="true" />

              <div className="enrollment-supervisor-section">
                <p className="enrollment-section-label">Assigned Human Supervisor</p>
                <div className="enrollment-supervisor-grid">
                  <label className="enrollment-field-label">
                    Assigned Mentor Name
                    <select
                      className="input-field enrollment-field-input enrollment-field-select"
                      value={enrollForm.mentorId}
                      onChange={(e) => selectAssignedMentor(e.target.value)}
                    >
                      <option value="">Assign later</option>
                      {enrollmentMentors.length === 0 ? (
                        <option value="" disabled>
                          No mentors on Mentor List yet
                        </option>
                      ) : (
                        enrollmentMentors.map((mentor) => (
                          <option key={mentor.id} value={String(mentor.id)}>
                            {mentor.name}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <label className="enrollment-field-label">
                    Mentor Contact Phone
                    <input
                      className="input-field enrollment-field-input"
                      type="tel"
                      value={enrollForm.mentorPhone}
                      readOnly={selectedMentorLocked}
                      onChange={(e) => updateEnroll('mentorPhone', e.target.value)}
                    />
                  </label>
                </div>
                <label className="enrollment-field-label enrollment-supervisor-email">
                  Mentor Email
                  <input
                    className="input-field enrollment-field-input"
                    type="email"
                    value={enrollForm.mentorEmail}
                    readOnly={selectedMentorLocked}
                    onChange={(e) => updateEnroll('mentorEmail', e.target.value)}
                  />
                </label>
                <p className="enrollment-supervisor-hint">
                  Choose a mentor from Mentor List. Email and phone fill in automatically.
                  Use Assign later if you will match them on Mentor List after enrolment.
                </p>
              </div>

              {enrollError && <p className="error-text enrollment-form-error">{enrollError}</p>}

              <div className="enrollment-action-row">
                <button type="submit" className="btn-primary enrollment-action-btn" disabled={loading}>
                  {loading ? 'Enrolling...' : 'Enroll Mentee'}
                </button>
                <button type="button" className="btn-primary enrollment-action-btn" onClick={resetEnrollForm}>
                  Reset Form
                </button>
              </div>
            </form>

            {newKey && (
              <div className="enrollment-key-display">
                <p className="org-profile-label">Active Mentee Key</p>
                <div className="key-display-box">{newKey}</div>
                <div className="org-email-action-stack enrollment-key-email-actions">
                  <button
                    type="button"
                    className="org-email-action-btn"
                    onClick={() => void copyMenteeEmail(newKey)}
                  >
                    COPY EMAIL
                  </button>
                  <button
                    type="button"
                    className="org-email-action-btn org-email-action-btn-send"
                    disabled={sendingEmailKey === `mentee-${newKey}` || !enrollForm.menteeEmail.trim()}
                    onClick={() => void sendMenteeEmail(newKey, enrollForm.menteeEmail.trim())}
                  >
                    {sendingEmailKey === `mentee-${newKey}` ? 'Opening…' : 'SEND TO MENTEE'}
                  </button>
                </div>
              </div>
            )}

            <table className="admin-table enrollment-mentee-table">
              <thead>
                <tr>
                  <th>Mentee</th>
                  <th>Email</th>
                  <th>ELT Key</th>
                  <th>Assigned Mentor</th>
                  <th>Status</th>
                  <th>Account State</th>
                  <th>Onboarding</th>
                  <th>Archive</th>
                </tr>
              </thead>
              <tbody>
                {dashboard?.keys?.map((k) => (
                  <tr key={k.mentee_token}>
                    <td>{k.mentee_name || '—'}</td>
                    <td>{k.mentee_email || k.email || '—'}</td>
                    <td>{k.mentee_token}</td>
                    <td>{k.mentor_name || '—'}</td>
                    <td>{k.status}</td>
                    <td>{k.account_state}</td>
                    <td>
                      <div className="org-email-action-stack org-email-action-stack-inline">
                        <button
                          type="button"
                          className="org-email-action-btn"
                          onClick={() => void copyMenteeEmail(k.mentee_token)}
                        >
                          COPY EMAIL
                        </button>
                        <button
                          type="button"
                          className="org-email-action-btn org-email-action-btn-send"
                          disabled={sendingEmailKey === `mentee-${k.mentee_token}` || !(k.mentee_email || k.email)}
                          onClick={() => void sendMenteeEmail(k.mentee_token, k.mentee_email || k.email)}
                        >
                          {sendingEmailKey === `mentee-${k.mentee_token}` ? 'Opening…' : 'SEND'}
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="mentor-archive-btn"
                        disabled={archiveActionLoading === `archive-mentee-${k.mentee_token}`}
                        onClick={() => handleArchiveMentee(k.mentee_token)}
                      >
                        {archiveActionLoading === `archive-mentee-${k.mentee_token}` ? '...' : 'ARCHIVE'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="enrollment-seat-metric">
              {activeSeats} of {seatLimit} seats in use.
            </p>
          </div>
        );
      }
      case 'Org Shared Pack':
        return (
          <div className="org-panel-card policy-vault-workspace">
            <h2 className="section-header policy-vault-heading">Org Shared Pack</h2>
            <p className="policy-vault-intro">
              Connect the organisation&apos;s own cloud folder for shared policies, procedures,
              and learning material. Mentology never hosts these files — they remain in your
              organisation&apos;s own Google Drive folder. All org mentees consume this shared pack.
              A RED Email / emergency protocol file is optional: if you do not select one, the app
              uses built-in protocols. Always set the emergency contact name and email in Guardian Feed.
            </p>

            <FileStoragePanel
              storageKey="org-admin"
              scope="org"
              returnTo="/org-admin?section=Org Shared Pack"
              defaultOpen
              folderPlaceholder={ORG_SHARED_FOLDER_PLACEHOLDER}
              folderLabelHeading="Org shared pack folder label"
              onPersist={persistOrgCloudStorage}
              onBeforeGoogleConnect={async () => { await persistProfile(true); }}
            />

            <div className="policy-vault-upload-stack">
              <div className="policy-vault-upload-line">
                <p className="policy-vault-upload-label">Select Policies, Procedures &amp; General Documents</p>
                <Tier2DocumentPicker
                  scope="org"
                  cloud={buildCloudStorageMeta(dashboard?.org?.cloud_storage || {})}
                  category="General"
                  buttonLabel="Select General Documents"
                  onFilesChange={(files) => {
                    setSharedPackFiles(files.map((file) => ({
                      id: file.drive_file_id,
                      filename: file.filename,
                      uploadedAt: file.selected_at || new Date().toLocaleString(),
                      category: (file.category === 'Emergency Protocol' ? 'Emergency Protocol' : 'General') as SharedPackFileCategory,
                    })));
                  }}
                />
              </div>

              <div className="policy-vault-upload-line policy-vault-upload-emergency">
                <p className="policy-vault-upload-label">Select RED Email / Emergency Protocols Document (optional)</p>
                <Tier2DocumentPicker
                  scope="org"
                  cloud={buildCloudStorageMeta(dashboard?.org?.cloud_storage || {})}
                  category="Emergency Protocol"
                  buttonLabel="Select Emergency Protocol"
                  onFilesChange={(files) => {
                    setSharedPackFiles(files.map((file) => ({
                      id: file.drive_file_id,
                      filename: file.filename,
                      uploadedAt: file.selected_at || new Date().toLocaleString(),
                      category: (file.category === 'Emergency Protocol' ? 'Emergency Protocol' : 'General') as SharedPackFileCategory,
                    })));
                  }}
                />
              </div>
            </div>

            <table className="admin-table policy-vault-file-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Stored in</th>
                  <th>Selected</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sharedPackFiles.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="policy-vault-empty-row">
                      No files chosen yet. Copy documents into Google Drive folder
                      {' '}
                      {dashboard?.org?.cloud_storage?.folder_label || 'AI_Mentor_Files'}
                      , then click Choose files and tick them.
                    </td>
                  </tr>
                ) : (
                  sharedPackFiles.map((file) => {
                    const localOnly = isLocalIntentFileId(file.id);
                    const folderLabel = dashboard?.org?.cloud_storage?.folder_label || 'Google Drive';
                    const folderUrl = googleDriveFolderUrl(dashboard?.org?.cloud_storage?.folder_id);
                    return (
                      <tr key={file.id}>
                        <td>{file.filename}</td>
                        <td>
                          {localOnly ? (
                            'Name only — file is still on your computer'
                          ) : (
                            <a href={folderUrl} target="_blank" rel="noreferrer">
                              {folderLabel}
                            </a>
                          )}
                        </td>
                        <td>{file.uploadedAt}</td>
                        <td>
                          <span className={`policy-vault-category ${file.category === 'Emergency Protocol' ? 'policy-vault-category-emergency' : ''}`}>
                            {file.category}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="policy-vault-remove-btn"
                            onClick={() => void removeSharedPackFile(file.id)}
                          >
                            REMOVE
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            <DeleteAccountBlock
              onDelete={async () => {
                try {
                  const result = await authApi.deleteData();
                  if (!result.success) {
                    alert(result.message || result.reason || 'Delete failed');
                    return;
                  }
                  alert(result.message || DELETE_ACCOUNT_SUCCESS_MESSAGE);
                  navigate('/', { replace: true });
                } catch (error: unknown) {
                  const err = error as { error?: string; message?: string };
                  alert(err.error || err.message || 'Delete failed');
                }
              }}
            />
          </div>
        );
      case 'Guardian Feed':
        return (
          <div className="guardian-feed-workspace">
            <div
              className="org-panel-card guardian-contacts-card guardian-contacts-unlocked"
              data-emergency-protocol-uploaded={isEmergencyProtocolUploaded}
            >
              <h3 className="guardian-card-title">RED Email Protocol Contacts</h3>

              <div
                className={`guardian-status-banner ${isEmergencyProtocolUploaded ? 'guardian-status-unlocked' : 'guardian-status-builtin'}`}
                role="status"
              >
                {isEmergencyProtocolUploaded
                  ? 'A Drive emergency protocol file is selected. Update the contact name and email below.'
                  : 'No protocol file selected — Mentology will use built-in RED Email protocols. Update the emergency contact name and email below.'}
              </div>

              <form className="guardian-contacts-form" onSubmit={updateRedPhoneContacts}>
                <fieldset className="guardian-contacts-fieldset">
                  <label className="guardian-field-label">
                    Emergency Contact Name
                    <input
                      className="input-field guardian-field-input"
                      type="text"
                      value={redPhoneContacts.emergencyContactName}
                      onChange={(e) => updateRedPhoneContact('emergencyContactName', e.target.value)}
                    />
                  </label>
                  <label className="guardian-field-label">
                    Emergency Lead Email
                    <input
                      className="input-field guardian-field-input"
                      type="email"
                      value={redPhoneContacts.emergencyLeadEmail}
                      onChange={(e) => updateRedPhoneContact('emergencyLeadEmail', e.target.value)}
                    />
                  </label>

                  <div className="guardian-contacts-actions">
                    <button
                      type="submit"
                      className="btn-primary guardian-action-btn"
                    >
                      Update Contacts
                    </button>
                    <button
                      type="button"
                      className="btn-primary guardian-action-btn"
                      onClick={resetRedPhoneContacts}
                    >
                      Reset
                    </button>
                  </div>
                  {redPhoneSaveStatus && (
                    <p className="guardian-save-status" role="status">{redPhoneSaveStatus}</p>
                  )}
                </fieldset>
              </form>
            </div>

            <div className="org-panel-card guardian-log-card">
              <h3 className="guardian-card-title">Guardian Feed Logs</h3>
              <table className="admin-table guardian-log-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Mentee</th>
                    <th>ELT Key</th>
                    <th>Alert Type</th>
                    <th>Assigned Mentor</th>
                    <th>Mentor Notified</th>
                  </tr>
                </thead>
                <tbody>
                  {guardianFeed.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="guardian-log-empty">
                        No RED Email alerts logged for enrolled Mentees.
                      </td>
                    </tr>
                  ) : (
                    guardianFeed.map((alert) => (
                      <tr key={alert.id} className={alert.mentor_notified ? 'guardian-row-alert' : ''}>
                        <td>{alert.created_at}</td>
                        <td>{alert.mentee_name || '—'}</td>
                        <td>{alert.mentee_token}</td>
                        <td>{alert.crisis_type}</td>
                        <td>{alert.mentor_name || alert.mentor_email || '—'}</td>
                        <td>{alert.mentor_notified ? `Yes (${alert.mentor_email})` : 'Pending'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'Support':
        return (
          <div className="org-panel-card support-workspace">
            <h2 className="section-header support-heading">Support</h2>
            <p className="support-intro">
              Lodge a support request. Tickets are emailed to <strong>support@mentology.com.au</strong>
              {' '}and also stored in Master Admin → System Data.
            </p>
            {supportSuccess && (
              <div className="support-success-banner" role="status">
                {supportSuccess}
              </div>
            )}
            <form onSubmit={submitSupportTicket} className="support-form">
              <label className="support-field-label">
                Name
                <input
                  className="input-field support-field-input"
                  type="text"
                  placeholder="The individual lodging the complaint"
                  value={supportForm.name}
                  onChange={(e) => updateSupport('name', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Email
                <input
                  className="input-field support-field-input"
                  type="email"
                  value={supportForm.email}
                  onChange={(e) => updateSupport('email', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Contact Phone
                <input
                  className="input-field support-field-input"
                  type="tel"
                  value={supportForm.phone}
                  onChange={(e) => updateSupport('phone', e.target.value)}
                  required
                />
              </label>
              <label className="support-field-label">
                Problem Description / Issue Details
                <textarea
                  className="input-field support-field-input support-textarea"
                  value={supportForm.description}
                  onChange={(e) => updateSupport('description', e.target.value)}
                  required
                  rows={3}
                />
              </label>
              <div
                className={`support-dropzone ${dragActive ? 'support-dropzone-active' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  addSupportFiles(e.dataTransfer.files);
                }}
              >
                <p className="support-dropzone-hint">Drag and drop files here, or use the picker below.</p>
                <label className="btn-primary support-file-label">
                  Attach Screenshots or Log Files
                  <input
                    type="file"
                    multiple
                    className="support-file-input"
                    onChange={(e) => addSupportFiles(e.target.files)}
                  />
                </label>
                {supportFiles.length > 0 && (
                  <ul className="support-file-list">
                    {supportFiles.map((file) => (
                      <li key={`${file.name}-${file.size}`}>{file.name} ({Math.round(file.size / 1024)} KB)</li>
                    ))}
                  </ul>
                )}
              </div>
              {supportError && <p className="error-text support-error">{supportError}</p>}
              <div className="support-action-row">
                <button type="submit" className="btn-primary support-action-btn" disabled={supportLoading}>
                  {supportLoading ? 'Transmitting...' : 'Submit Support Ticket'}
                </button>
              </div>
            </form>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-layout" data-screen-id={SCREENS.ELT06}>
      <aside className="admin-sidebar">
        <img
          src={encodeURI('/Assets/logo/Mentology Logo.png')}
          alt="Mentology"
          className="org-sidebar-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <p className="org-sidebar-heading">Mentoring Admin Control</p>
        {SIDEBAR_SECTIONS.map((section) => (
          <div key={section} className="admin-nav-group">
            <button
              type="button"
              className={`admin-nav-item ${activeSection === section && (section !== 'Enrollment' || enrollmentSubview === 'default') ? 'active' : ''}`}
              onClick={() => {
                setActiveSection(section);
                if (section === 'Enrollment') setEnrollmentSubview('default');
              }}
            >
              {section}
            </button>
            {section === 'Enrollment' && ENROLLMENT_SUB_NAV.map((subItem) => (
              <button
                key={subItem}
                type="button"
                className={`admin-nav-item admin-nav-subitem ${
                  activeSection === 'Enrollment' && enrollmentSubview === (subItem === 'Mentor List' ? 'mentor-list' : 'archives')
                    ? 'active'
                    : ''
                }`}
                onClick={() => {
                  setActiveSection('Enrollment');
                  setEnrollmentSubview(subItem === 'Mentor List' ? 'mentor-list' : 'archives');
                  setMentorFormError('');
                  setMentorAssignError('');
                }}
              >
                {subItem}
              </button>
            ))}
          </div>
        ))}
        <button type="button" className="admin-nav-item logout" onClick={() => navigate('/')}>Logout</button>
      </aside>
      <main className="admin-panel">
        {clientKey && (
          <div className="org-client-key-banner">
            <span className="org-client-key-label">Active Client Key:</span>
            <span className="org-client-key-value">{clientKey}</span>
          </div>
        )}
        {dashboardLoading && !dashboard ? (
          <p className="scannable-bullet-text">Loading organisation console...</p>
        ) : dashboardError && !dashboard ? (
          <div className="org-dashboard-error">
            <p className="scannable-bullet-text">{dashboardError}</p>
            <button
              type="button"
              className="admin-primary-btn"
              onClick={() => {
                loadDashboard().catch(() => undefined);
              }}
            >
              Retry
            </button>
          </div>
        ) : dashboard ? (
          renderPanel()
        ) : (
          <p className="scannable-bullet-text">Organisation console unavailable.</p>
        )}
      </main>
      {desktopIconOpen && (
        <DesktopAppIconModal
          titleId="org-admin-desktop-icon-title"
          onClose={() => setDesktopIconOpen(false)}
        />
      )}
    </div>
  );
}
